import React, { Fragment } from 'react';
import type { v2 } from '@deque/billing-service-client';
import { useTranslation } from 'react-i18next';

import { ProductSlugs } from '../../../../../common/constants';
import styles from '../IntegrationConfigurations.css';
import { type AuthUser } from '../../../../../common/contexts/auth';
import { IntegrationLogo } from '../../../integrations/IntegrationLogo';
import { useProducts } from '../../../../../common/contexts/products';
import { type ProductSlugWithConnections } from '../IntegrationConfigurations';
import { getJiraParagonActions } from '../../../../utils/getJiraParagonActions';
import { ConnectionButtonsLine } from '../ConnectionButtonsLine';

export interface JiraParagonIntegrationConfigurationsProps {
  connectionByProductSlug: ProductSlugWithConnections;
  activeEnterprise: v2.Enterprise;
  isEnterpriseAdmin: boolean;
  authUser: AuthUser;
  reloadConnections: () => void;
}

function JiraParagonIntegrationConfigurations({
  connectionByProductSlug,
  activeEnterprise,
  isEnterpriseAdmin,
  authUser,
  reloadConnections
}: JiraParagonIntegrationConfigurationsProps) {
  const { t } = useTranslation();

  const { getProductBySlug } = useProducts();

  return (
    <Fragment key={connectionByProductSlug.productSlug}>
      <div className={styles.productItemWrap}>
        <div className={styles.productLogo}>
          <IntegrationLogo
            integrationProductSlug={connectionByProductSlug.productSlug}
          />
        </div>

        <div className={styles.connectionItemDescription}>
          <h3>{connectionByProductSlug.integrationProductInfo.displayName}</h3>
          <p>
            {t(
              "Boost your team's accessibility efforts with seamless issue tracking directly from {{devtoolsProductName}} to {{integrationDisplayName}}",
              {
                devtoolsProductName: getProductBySlug(
                  ProductSlugs.axeDevToolsExtension
                )?.name,
                integrationDisplayName:
                  connectionByProductSlug.integrationProductInfo.displayName
              }
            )}
          </p>
          {connectionByProductSlug.connections.map(connection => (
            <div className={styles.connectionItemWrap} key={connection.id}>
              <h4>
                {t('Connection to {{baseUrl}}', {
                  baseUrl: connection.base_url
                })}
              </h4>
              <ConnectionButtonsLine
                key={connection.id}
                connection={connection}
                integrationProductInfo={
                  connectionByProductSlug.integrationProductInfo
                }
                enterprise={activeEnterprise}
                canManageIntegrations={isEnterpriseAdmin}
                authUser={authUser}
                connectionsUpdated={reloadConnections}
                getActions={getJiraParagonActions}
              />
            </div>
          ))}
        </div>
      </div>
      <ConnectionButtonsLine
        key={`new-${connectionByProductSlug.integrationProductInfo.paragonIntegrationName}-connection`}
        connection={null}
        integrationProductInfo={connectionByProductSlug.integrationProductInfo}
        enterprise={activeEnterprise}
        canManageIntegrations={isEnterpriseAdmin}
        authUser={authUser}
        connectionsUpdated={reloadConnections}
        getActions={getJiraParagonActions}
      />
    </Fragment>
  );
}

export default JiraParagonIntegrationConfigurations;
